import { type Breakpoint, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

export const TOOL_SIDEBAR_WIDTH = 42;
export const DIALOG_MAX_WIDTH_BREAKPOINT: Breakpoint = 'lg';

const DIALOG_VIEWPORT_MARGIN = 64;
const CONTENT_HORIZONTAL_PADDING = 48;
const CONTENT_VERTICAL_PADDING = 28;
const CONTENT_ROW_GAP = 16;
const PANEL_GAP = 24;
const SCROLLBAR_ALLOWANCE = 16;
const TITLE_HEIGHT = 56;
const TOOLBAR_HEIGHT = 40;
const SLIDER_BLOCK_HEIGHT = 72;
const ACTIONS_HEIGHT = 64;
const FILMSTRIP_HEIGHT = 48;

const MIN_CANVAS_WIDTH = 240;
const MIN_CANVAS_HEIGHT = 180;
const MAX_CANVAS_HEIGHT = 450;

const HORIZONTAL_CHROME_WIDTH = CONTENT_HORIZONTAL_PADDING + TOOL_SIDEBAR_WIDTH + PANEL_GAP + SCROLLBAR_ALLOWANCE;

export type EditorCanvasBounds = {
  maxWidth: number;
  maxHeight: number;
};

export const fitWithinBounds = (natural: { width: number; height: number }, bounds: EditorCanvasBounds) => {
  if (natural.width === 0 || natural.height === 0) return { width: 0, height: 0 };
  const scale = Math.min(bounds.maxWidth / natural.width, bounds.maxHeight / natural.height, 1);
  return { width: Math.round(natural.width * scale), height: Math.round(natural.height * scale) };
};

const computeBounds = (hasFilmstrip: boolean, dialogMaxWidth: number): EditorCanvasBounds => {
  const dialogWidth = Math.min(globalThis.innerWidth - DIALOG_VIEWPORT_MARGIN, dialogMaxWidth);
  const panelsWidth = dialogWidth - HORIZONTAL_CHROME_WIDTH;

  const chromeHeight =
    TITLE_HEIGHT +
    CONTENT_VERTICAL_PADDING +
    TOOLBAR_HEIGHT +
    CONTENT_ROW_GAP +
    SLIDER_BLOCK_HEIGHT +
    ACTIONS_HEIGHT +
    (hasFilmstrip ? CONTENT_ROW_GAP + FILMSTRIP_HEIGHT : 0);
  const panelHeight = globalThis.innerHeight - DIALOG_VIEWPORT_MARGIN - chromeHeight;

  return {
    maxWidth: Math.max(Math.floor(panelsWidth / 2), MIN_CANVAS_WIDTH),
    maxHeight: Math.min(Math.max(Math.floor(panelHeight), MIN_CANVAS_HEIGHT), MAX_CANVAS_HEIGHT),
  };
};

export const useEditorCanvasBounds = (hasFilmstrip: boolean): EditorCanvasBounds => {
  const dialogMaxWidth = useTheme().breakpoints.values[DIALOG_MAX_WIDTH_BREAKPOINT];
  const [bounds, setBounds] = useState(() => computeBounds(hasFilmstrip, dialogMaxWidth));

  useEffect(() => {
    const updateBounds = () =>
      setBounds(previous => {
        const next = computeBounds(hasFilmstrip, dialogMaxWidth);
        return previous.maxWidth === next.maxWidth && previous.maxHeight === next.maxHeight ? previous : next;
      });
    updateBounds();
    globalThis.addEventListener('resize', updateBounds);
    return () => globalThis.removeEventListener('resize', updateBounds);
  }, [hasFilmstrip, dialogMaxWidth]);

  return bounds;
};
